import { useState } from "react";
import I from "./Icons";
import GraphCard from "./GraphCard";
import MiniChart from "./MiniChart";
import { LAYOUTS, INSIGHTS_POS, G } from "./constants";
import { useTables } from "../report-builder";

interface PageCardProps {
  page: any;
  onUpdate: (page: any) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  sectionRepeat: string;
  sectionRepeats?: string[];
  pageNum: number;
  theme: any;
}

interface InsightsPreviewProps {
  w?: number;
  h?: number;
}

// ─── Page Card ───
function PageCard({ page, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast, sectionRepeat, sectionRepeats, pageNum, theme }: PageCardProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const TABLES = useTables();
  const li = LAYOUTS.find(l => l.id === page.layout) || LAYOUTS[0];
  const upG = (gid: string, d: any) => onUpdate({ ...page, graphs: page.graphs.map((g: any) => g.id === gid ? d : g) });
  const delG = (gid: string) => {
    if (page.graphs.length <= 1) return;
    onUpdate({ ...page, graphs: page.graphs.filter((g: any) => g.id !== gid) });
  };
  const moveG = (gid: string, dir: number) => {
    const graphs = [...page.graphs];
    const i = graphs.findIndex((g: any) => g.id === gid);
    const j = i + dir;
    if (j < 0 || j >= graphs.length) return;
    [graphs[i], graphs[j]] = [graphs[j], graphs[i]];
    onUpdate({ ...page, graphs });
  };
  const dupG = (gid: string) => {
    const graphs = [...page.graphs];
    const i = graphs.findIndex((g: any) => g.id === gid);
    const copy = JSON.parse(JSON.stringify(graphs[i]));
    copy.id = require("./constants").uid();
    copy.label += " (Copy)";
    graphs.splice(i + 1, 0, copy);
    onUpdate({ ...page, graphs });
  };
  const addG = () => {
    // Use first valid table and field from loaded datasource
    const firstTable = TABLES.length > 0 ? TABLES[0].id : "revenue";
    const firstField = TABLES.length > 0 && TABLES[0].numeric_fields?.length > 0 
      ? TABLES[0].numeric_fields[0] 
      : "revenue_amount";
    onUpdate({ 
      ...page, 
      graphs: [...page.graphs, G("Graph " + (page.graphs.length + 1), "stacked_bar", firstTable, firstField)] 
    });
  };
  const gColors = [theme.blue, theme.success, theme.warning, theme.error];
  const hasInsights = page.insights && page.insights !== "none";
  const insightsPos = page.insights || "none";

  // Mini insights preview block
  const InsightsPreview = ({ w = 100, h = 50 }: InsightsPreviewProps) => (
    <div style={{ 
      background: theme.background, 
      borderRadius: 5, 
      padding: "6px 4px", 
      textAlign: "center", 
      border: `1px solid ${theme.purple}40`, 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center", 
      justifyContent: "center", 
      minHeight: h 
    }}>
      <I.Bulb />
      <div style={{ fontSize: 9, fontWeight: 600, color: theme.purple, marginTop: 2 }}>Insights</div>
      <svg width={w - 16} height={18} viewBox={`0 0 ${w - 16} 18`}>
        {[0, 1, 2].map(r => (
          <rect 
            key={r} 
            x="2" 
            y={2 + r * 6} 
            width={20 + Math.random() * (w - 40)} 
            height="3.5" 
            rx="1" 
            fill={theme.purple} 
            opacity={0.12 + r * 0.05} 
          />
        ))}
      </svg>
    </div>
  );

  return (
    <div style={{ 
      background: theme.background, 
      border: `1px solid ${theme.border}`, 
      borderRadius: 10, 
      overflow: "hidden" 
    }}>
      <div 
        style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 8, 
          padding: "9px 14px", 
          cursor: "pointer", 
          userSelect: "none" 
        }} 
        onClick={() => setExpanded(!expanded)}
      >
        <span style={{ color: theme.textSecondary }}>
          {expanded ? <I.Down /> : <I.Right />}
        </span>
        <div style={{ 
          width: 28, 
          height: 22, 
          borderRadius: 5, 
          background: theme.cardBackground, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          fontSize: 11, 
          fontWeight: 700, 
          color: theme.textSecondary, 
          flexShrink: 0 
        }}>
          P{pageNum}
        </div>
        <input 
          value={page.name} 
          onChange={e => onUpdate({ ...page, name: e.target.value })} 
          onClick={e => e.stopPropagation()} 
          style={{ 
            flex: 1, 
            background: "transparent", 
            border: "none", 
            color: theme.text, 
            fontSize: 14, 
            fontWeight: 600, 
            padding: 0, 
            outline: "none"
          }} 
        />
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: theme.textSecondary }}>
          {hasInsights && (
            <span style={{
              display: "inline-flex", 
              alignItems: "center", 
              gap: 3, 
              fontSize: 11, 
              padding: "1px 6px", 
              borderRadius: 8, 
              background: `${theme.purple}20`, 
              color: theme.purple, 
              fontWeight: 600 
            }}>
              <I.Bulb /> Insights
            </span>
          )}
          <I.Grid />
          <span>{page.graphs.length} graph{page.graphs.length !== 1 ? "s" : ""} · {li.name}</span>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onMoveUp(); }}
          disabled={isFirst}
          style={{ background: "none", border: "none", color: isFirst ? theme.border : theme.textSecondary, cursor: isFirst ? "default" : "pointer", padding: 3, display: "flex" }}
        >
          <I.Up />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onMoveDown(); }}
          disabled={isLast}
          style={{ background: "none", border: "none", color: isLast ? theme.border : theme.textSecondary, cursor: isLast ? "default" : "pointer", padding: 3, display: "flex" }}
        >
          <I.Dn />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onDuplicate(); }}
          style={{ background: "none", border: "none", color: theme.textSecondary, cursor: "pointer", padding: 3, display: "flex" }}
        >
          <I.Copy />
        </button>
        <button
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            background: "none",
            border: "none",
            color: theme.textSecondary,
            cursor: "pointer",
            padding: 3,
            display: "flex"
          }}
          onMouseEnter={e => e.currentTarget.style.color = theme.error}
          onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
        >
          <I.Trash />
        </button>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${theme.border}`, padding: "10px 14px 14px" }}>
          {/* Layout + Insights row */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ 
                fontSize: 11, 
                fontWeight: 700, 
                textTransform: "uppercase", 
                letterSpacing: ".08em", 
                color: theme.textSecondary
              }}>
                Layout
              </span>
              {LAYOUTS.map(l => (
                <button 
                  key={l.id} 
                  onClick={() => onUpdate({ ...page, layout: l.id })} 
                  style={{ 
                    padding: "3px 10px", 
                    borderRadius: 5, 
                    fontSize: 12, 
                    fontWeight: 600, 
                    cursor: "pointer", 
                    background: page.layout === l.id ? `${theme.blue}20` : theme.cardBackground, 
                    border: `1px solid ${page.layout === l.id ? theme.blue : theme.border}`, 
                    color: page.layout === l.id ? theme.blue : theme.textSecondary
                  }}
                >
                  {l.name}
                </button>
              ))}
            </div>
            <div style={{ width: 1, height: 20, background: theme.border }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ 
                fontSize: 11, 
                fontWeight: 700, 
                textTransform: "uppercase", 
                letterSpacing: ".08em", 
                color: theme.textSecondary
              }}>
                Insights
              </span>
              {INSIGHTS_POS.map(ip => (
                <button
                  key={ip.id}
                  onClick={() => onUpdate({ ...page, insights: ip.id })}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 5,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: insightsPos === ip.id ? `${theme.purple}20` : theme.cardBackground,
                    border: `1px solid ${insightsPos === ip.id ? theme.purple : theme.border}`,
                    color: insightsPos === ip.id ? theme.purple : theme.textSecondary
                  }}
                >
                  {ip.name}
                </button>
              ))}
              {insightsPos !== "none" && (
                <span
                  title={
                    sectionRepeat && sectionRepeat !== "None"
                      ? `Insights will be matched by: ${[sectionRepeat, ...(sectionRepeats || []).filter((r: string) => r !== sectionRepeat && r !== "None")].join(", ")}. More specific insights (matching more dimensions) take priority.`
                      : "No repeat fields set — only company-wide insights (with no dimensions) will appear."
                  }
                  style={{ cursor: "help", fontSize: 14, color: theme.textSecondary, marginLeft: 2 }}
                >
                  &#9432;
                </span>
              )}
            </div>
            <div style={{ width: 1, height: 20, background: theme.border }} />
            <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={page.fitGraphs !== false}
                onChange={() => onUpdate({ ...page, fitGraphs: page.fitGraphs === false })}
                style={{ accentColor: theme.blue }}
              />
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: theme.textSecondary }}>
                Fit Graphs
              </span>
            </label>
          </div>
          {/* Visual preview */}
          <div style={{ 
            marginBottom: 12, 
            padding: 8, 
            background: theme.navbarBackground, 
            borderRadius: 8, 
            border: `1px solid ${theme.border}` 
          }}>
            {insightsPos === "right" ? (
              <div style={{ display: "grid", gridTemplateColumns: "65% 35%", gap: 6 }}>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${li.cols}, 1fr)`, gap: 6 }}>
                  {page.graphs.map((g: any, gi: number) => (
                    <div 
                      key={g.id} 
                      style={{ 
                        background: theme.cardBackground, 
                        borderRadius: 5, 
                        padding: "6px 4px", 
                        textAlign: "center", 
                        border: `1px solid ${theme.border}` 
                      }}
                    >
                      <div style={{ 
                        fontSize: 10, 
                        fontWeight: 600, 
                        color: theme.textSecondary, 
                        marginBottom: 2, 
                        overflow: "hidden", 
                        textOverflow: "ellipsis", 
                        whiteSpace: "nowrap" 
                      }}>
                        {g.label}
                      </div>
                      <MiniChart 
                        type={g.chartType} 
                        w={li.cols > 2 ? 60 : li.cols > 1 ? 80 : 120} 
                        h={li.cols > 2 ? 28 : 36} 
                        color={gColors[gi % 4]} 
                      />
                      {g.filterContext !== "None" && (
                        <div style={{ fontSize: 9, color: theme.warning, marginTop: 1 }}>⚡ filtered</div>
                      )}
                    </div>
                  ))}
                </div>
                <InsightsPreview w={120} h={page.graphs.length > 2 ? 100 : 70} />
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${li.cols}, 1fr)`, gap: 6 }}>
                  {page.graphs.map((g: any, gi: number) => (
                    <div 
                      key={g.id} 
                      style={{ 
                        background: theme.cardBackground, 
                        borderRadius: 5, 
                        padding: "6px 4px", 
                        textAlign: "center", 
                        border: `1px solid ${theme.border}` 
                      }}
                    >
                      <div style={{ 
                        fontSize: 10, 
                        fontWeight: 600, 
                        color: theme.textSecondary, 
                        marginBottom: 2, 
                        overflow: "hidden", 
                        textOverflow: "ellipsis", 
                        whiteSpace: "nowrap" 
                      }}>
                        {g.label}
                      </div>
                      <MiniChart 
                        type={g.chartType} 
                        w={li.cols > 2 ? 80 : li.cols > 1 ? 110 : 180} 
                        h={li.cols > 2 ? 32 : 40} 
                        color={gColors[gi % 4]} 
                      />
                      {g.filterContext !== "None" && (
                        <div style={{ fontSize: 9, color: theme.warning, marginTop: 1 }}>⚡ filtered</div>
                      )}
                    </div>
                  ))}
                </div>
                {insightsPos === "bottom" && (
                  <div style={{ marginTop: 6 }}>
                    <InsightsPreview w={300} h={40} />
                  </div>
                )}
              </>
            )}
          </div>
          {/* Graph list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {page.graphs.map((g: any, gi: number) => (
              <GraphCard
                key={g.id}
                graph={g}
                onUpdate={(d: any) => upG(g.id, d)}
                onDelete={() => delG(g.id)}
                onDuplicate={() => dupG(g.id)}
                onMoveUp={() => moveG(g.id, -1)}
                onMoveDown={() => moveG(g.id, 1)}
                isFirst={gi === 0}
                isLast={gi === page.graphs.length - 1}
                canDelete={page.graphs.length > 1}
                theme={theme}
                sectionRepeat={sectionRepeat}
                sectionRepeats={sectionRepeats}
              />
            ))}
          </div>
          <button 
            onClick={addG} 
            style={{ 
              width: "100%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              gap: 5, 
              padding: "8px", 
              marginTop: 8, 
              borderRadius: 6, 
              cursor: "pointer", 
              background: "none", 
              border: `1.5px dashed ${theme.border}`, 
              color: theme.textSecondary, 
              fontSize: 13, 
              fontWeight: 500 
            }}
            onMouseEnter={e => { 
              e.currentTarget.style.borderColor = theme.blue; 
              e.currentTarget.style.color = theme.blue; 
            }} 
            onMouseLeave={e => { 
              e.currentTarget.style.borderColor = theme.border; 
              e.currentTarget.style.color = theme.textSecondary; 
            }}
          >
            <I.Plus /> Add Graph to Page
          </button>
        </div>
      )}
    </div>
  );
}

export default PageCard;
